/**
 * The supplier invoice matching panel of one purchase order (batch 8 T12c3, #3033): live coverage over ALL documents, the documents
 * (a true total and paging, not the first page only), what blocks line matching (the old header match, unsupported legacy receipts,
 * unknown VAT) and the entry points to the entry and detail dialogs.
 *
 * Standalone and meant to be embedded by the purchase order detail screen (T15). It makes no request without the right to read
 * financial matching data, shows nothing privileged after a session or permission change (owner boundary), and shows the server's
 * numbers as they are: it decides nothing about matching itself.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useInvoiceFacts, useInvoiceList, useInvoiceOperations, type InvoiceFacts } from '@/hooks/useSupplierInvoices';
import { InvoiceBoundary, useStillShown } from './InvoiceBoundary';
import { CoverageSummary, PendingOperations, Section } from './InvoiceParts';
import { SupplierInvoiceDetailDialog } from './SupplierInvoiceDetailDialog';
import { SupplierInvoiceDialog } from './SupplierInvoiceDialog';
import { useInvoiceAccess } from './invoiceSession';
import { NS, StatusChip, ViolationList, useErrorText, useFormatters } from './invoiceView';

export interface SupplierInvoicesPanelProps {
  purchaseOrderId: string;
}

/** Standalone: enforces the owner, rights and purchase order boundary itself. */
export function SupplierInvoicesPanel({ purchaseOrderId }: SupplierInvoicesPanelProps) {
  return (
    <InvoiceBoundary target={purchaseOrderId}>
      <Panel purchaseOrderId={purchaseOrderId} />
    </InvoiceBoundary>
  );
}

function Panel({ purchaseOrderId }: SupplierInvoicesPanelProps) {
  const { t } = useTranslation(NS);
  const access = useInvoiceAccess();
  const stillShown = useStillShown();
  const facts = useInvoiceFacts(purchaseOrderId);
  const list = useInvoiceList(purchaseOrderId);
  const ops = useInvoiceOperations(purchaseOrderId, stillShown);
  const errorText = useErrorText();
  const fmt = useFormatters();
  const [entryOpen, setEntryOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  if (!access.canRead) {
    return (
      <Alert severity="info" data-testid="invoice-access-denied">
        {t('access.noRead')}
      </Alert>
    );
  }

  const unknownVatLines = (facts.data?.lines ?? []).filter((line) => line.recordedVatPercent === null);

  return (
    <Paper variant="outlined" sx={{ p: 2 }} component="section" aria-label={t('panel.title')} data-testid="supplier-invoices-panel">
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6" component="h2">
          {t('panel.title')}
        </Typography>
        {access.canMatch && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEntryOpen(true)} disabled={facts.data !== undefined && !facts.data.purchaseOrder.lineFlowAllowed}>
            {t('panel.add')}
          </Button>
        )}
      </Stack>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('panel.intro')}
        </Typography>
        <PendingOperations ops={ops} />
        {facts.isLoading && (
          <Stack direction="row" spacing={1} alignItems="center" role="status">
            <CircularProgress size={18} />
            <Typography variant="body2">{t('loading.facts')}</Typography>
          </Stack>
        )}
        {facts.isError && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => facts.refetch()}>{t('retryRead')}</Button>}>
            {errorText(facts.error).message}
          </Alert>
        )}
        {facts.data && <Blockers facts={facts.data} unknownVatCount={unknownVatLines.length} />}
        {facts.data && (
          <Section title={t('panel.coverageTitle')}>
            <CoverageSummary coverage={facts.data.coverage} poLines={facts.data.lines} />
          </Section>
        )}
        <Section title={t('panel.documentsTitle')}>
          {list.isLoading && (
            <Stack direction="row" spacing={1} alignItems="center" role="status">
              <CircularProgress size={18} />
              <Typography variant="body2">{t('loading.list')}</Typography>
            </Stack>
          )}
          {list.isError && (
            <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => list.refetch()}>{t('retryRead')}</Button>}>
              {errorText(list.error).message}
            </Alert>
          )}
          {!list.isLoading && !list.isError && list.items.length === 0 && <Typography variant="body2">{t('panel.empty')}</Typography>}
          {list.items.length > 0 && (
            <TableContainer>
              <Table size="small" aria-label={t('panel.documentsAria')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('panel.columns.number')}</TableCell>
                    <TableCell>{t('panel.columns.kind')}</TableCell>
                    <TableCell>{t('panel.columns.date')}</TableCell>
                    <TableCell>{t('panel.columns.status')}</TableCell>
                    <TableCell align="right">{t('panel.columns.gross')}</TableCell>
                    <TableCell align="right">{t('panel.columns.lines')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.items.map((item) => (
                    <TableRow key={item.id} data-testid={`document-${item.invoiceNumber}`}>
                      <TableCell>{item.invoiceNumber}</TableCell>
                      <TableCell>{t(`form.kind.${item.documentKind}`)}</TableCell>
                      <TableCell>{fmt.date(item.invoiceDate)}</TableCell>
                      <TableCell>
                        <StatusChip status={item.status} />
                      </TableCell>
                      <TableCell align="right">{fmt.cents(item.grossTotalCents)}</TableCell>
                      <TableCell align="right">{item.lineCount}</TableCell>
                      <TableCell align="right">
                        <Button size="small" aria-label={t('panel.open', { number: item.invoiceNumber })} onClick={() => setDetailId(item.id)}>
                          {t('panel.openShort')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {list.total !== null && list.items.length > 0 && (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" data-testid="document-count">
                {t('panel.showing', { shown: list.items.length, total: list.total })}
              </Typography>
              {list.hasMore && (
                <Button size="small" disabled={list.isFetchingMore} onClick={() => list.loadMore()}>
                  {list.isFetchingMore ? t('loading.list') : t('panel.loadMore')}
                </Button>
              )}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('panel.numberReserved')}
          </Typography>
        </Section>
      </Stack>
      {entryOpen && <SupplierInvoiceDialog open purchaseOrderId={purchaseOrderId} onClose={() => setEntryOpen(false)} />}
      {detailId !== null && <SupplierInvoiceDetailDialog open invoiceId={detailId} purchaseOrderId={purchaseOrderId} onClose={() => setDetailId(null)} />}
    </Paper>
  );
}

function Blockers({ facts, unknownVatCount }: { facts: InvoiceFacts; unknownVatCount: number }) {
  const { t } = useTranslation(NS);
  const { purchaseOrder } = facts;
  const coverage = facts.coverage.ok ? facts.coverage.result : null;
  return (
    <Box>
      <Stack spacing={1}>
        {!purchaseOrder.lineFlowAllowed && (
          <Alert severity="error" data-testid="flow-blocked">
            <AlertTitle>{purchaseOrder.invoiceMatchLevel === 'header' ? t('flow.headerTitle') : t('flow.blockedTitle')}</AlertTitle>
            {t('flow.blockedBody')}
            <ViolationList violations={purchaseOrder.lineFlowViolations} />
          </Alert>
        )}
        {coverage && coverage.unsupportedLegacyQuantity > 0 && (
          <Alert severity="warning" data-testid="legacy-unsupported">
            {t('flow.legacyReceipts', { count: coverage.unsupportedLegacyQuantity })}
          </Alert>
        )}
        {unknownVatCount > 0 && (
          <Alert severity="warning" data-testid="vat-unknown">
            {t('flow.vatUnknown', { count: unknownVatCount })}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
